import React, { useEffect } from "react"
import { Select } from "@material-ui/core"
import InputLabel from "@material-ui/core/InputLabel"
import MenuItem from "@material-ui/core/MenuItem"
import FormControl from "@material-ui/core/FormControl"
import FormHelperText from "@material-ui/core/FormHelperText"

import { fetchBruksklasser } from "../../api"

import { FormField } from "../../styles/Form"

import { Bruksklasse, ArbeidAction, ArbeidModel } from "../../store/arbeid"
import { UIModel } from "../../store/ui"
import {
  touchField,
  Field,
  updateValue,
} from "@nvdb-rapport/shared/src/components/formTypes"
import { Maybe, number } from "purify-ts"

export enum BruksklasseFilter {
  ONLY_OFFICIAL = "ONLY_OFFICIAL",
  ONLY_UNOFFICIAL = "ONLY_UNOFFICIAL",
  SHOW_ALL = "SHOW_ALL",
}

interface Props {
  filter?: BruksklasseFilter
  arbeidState: ArbeidModel
  arbeidDispatch: React.Dispatch<ArbeidAction>
  setSelectedBruksklasse: React.Dispatch<Field<Bruksklasse | undefined>>
  uiState: UIModel
  selectedBruksklasse: Field<Bruksklasse | undefined>
}

function BruksklasseSelect({
  filter = BruksklasseFilter.SHOW_ALL,
  uiState,
  setSelectedBruksklasse,
  arbeidState,
  arbeidDispatch,
  selectedBruksklasse,
}: Props) {
  const language = uiState.language

  const bruksklasser = arbeidState.bruksklasser

  //   FETCH BRUKSKLASSER
  useEffect(() => {
    async function asyncCallback() {
      const result = await fetchBruksklasser()
      const data: Bruksklasse[] = await result.json()
      const officialBruksklasser = data
      arbeidDispatch({ type: "SetBruksklasser", payload: officialBruksklasser })
    }
    asyncCallback()
  }, [arbeidDispatch])

  const filteredBruksklasser: Bruksklasse[] = bruksklasser.filter(
    bruksklasse => {
      if (filter === BruksklasseFilter.ONLY_UNOFFICIAL) {
        return bruksklasse.offisiell === false
      } else if (filter === BruksklasseFilter.ONLY_OFFICIAL) {
        return bruksklasse.offisiell === true
      }
      return true
    }
  )

  return (
    <FormField>
      <FormControl required variant="filled" style={{ width: "100%" }}>
        <InputLabel style={{ textAlign: "left" }}>Bruksklasse</InputLabel>
        <Select
          name="bruksklasseSelect"
          style={{ textAlign: "left", fontSize: "1rem" }}
          labelId="bruksklasse-select-label"
          error={
            selectedBruksklasse.touched &&
            selectedBruksklasse.value === undefined
          }
          value={selectedBruksklasse.value}
          onChange={event => {
            const decodedString = number.decode(event.target.value)

            decodedString.caseOf({
              Left: e => {
                console.log("Ugyldig bruksklasse", e)
              },
              Right: value => {
                const selectedBK = selectBruksklasse(bruksklasser, value)
                selectedBK.caseOf({
                  Nothing: () => {
                    console.log("Bruksklasse not found")
                  },
                  Just: selected => {
                    setSelectedBruksklasse(
                      touchField(
                        updateValue(arbeidState.selectedBruksklasse, selected)
                      )
                    )
                  },
                })
              },
            })
          }}
        >
          {filteredBruksklasser.map(({ id, navn }) => (
            <MenuItem key={id} value={id}>
              {navn}
            </MenuItem>
          ))}
        </Select>

        <FormHelperText>
          {language === "nb_NO"
            ? "Bruksklasse må velges"
            : "Bruksklasse må veljas"}
        </FormHelperText>
      </FormControl>
    </FormField>
  )
}

function selectBruksklasse(
  allBruksklasser: Bruksklasse[],
  id: number
): Maybe<Bruksklasse> {
  return Maybe.fromNullable(
    allBruksklasser.find(bruksklasse => bruksklasse.id === id)
  )
}

export default BruksklasseSelect
